import type { LiveBreakWidgetCopy } from "@loot-os/api-contract";
import chevronUpIcon from "@loot-os/icons/icons/chevron-up";
import { type ReactNode, type Ref, useEffect, useRef } from "react";

import { Icon } from "../../core/Icon.js";
import { Text } from "../../core/Text.js";
import { cn } from "../../foundations/cn.js";
import { FOCUS_RING, PRESS, PRESS_SCALE } from "../../foundations/interaction.js";
import { useLayout } from "../../foundations/themeContext.js";
import { LiveBreakWidgetReturnRow } from "./LiveBreakWidgetReturnRow.js";
import { LiveBreakWidgetStreamRow } from "./LiveBreakWidgetStreamRow.js";
import {
  type LiveBreakWidgetHosting,
  type LiveBreakWidgetPickerSections,
  type LiveBreakWidgetQueue,
  type LiveBreakWidgetStream,
  widgetSurface,
} from "./liveBreakWidget.js";

export interface LiveBreaksWidgetProps {
  words: LiveBreakWidgetCopy;
  /**
   * The stream row (`resolveWidgetStream`): a sponsored or pinned live break, or a promoted
   * stream. Null or left out, there is none: on a live break's page, while picture-in-picture
   * plays, after Hide widget, or with nothing sponsored on air.
   */
  stream?: LiveBreakWidgetStream | null;
  /** The stream row folded to its pill: the shopper's setting, kept per `stream.key` by the app. */
  collapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
  /** The stream picker's groups (`widgetPickerSections`); it shows with more than one to choose. */
  picker?: LiveBreakWidgetPickerSections | null;
  /** A pick from the picker: a live break to pin, or a promoted stream to select. */
  onPick?: (stream: LiveBreakWidgetStream) => void;
  /** View → Picture-in-picture; left out (no Twitch player here), the menu leaves it out. */
  onOpenPip?: (stream: LiveBreakWidgetStream) => void;
  /** View → Hide widget. */
  onHide?: () => void;
  /** The shopper's own open live break; shown in place of the queue row. */
  hosting?: LiveBreakWidgetHosting | null;
  /** The shopper's soonest place in a queue; shown only without a hosting row. */
  queue?: LiveBreakWidgetQueue | null;
  /**
   * The picture-in-picture frame (`LiveBreakPipFrame`), above the rows. It keeps one place in the
   * tree while rows come and go, so the app mounting the widget once keeps one player: navigating
   * never remounts it, and a remount restarts the stream.
   */
  pip?: ReactNode;
  /**
   * `dock`: fixed at the page's bottom-left corner, above the phone's tab bar on the right, 560
   * wide while picture-in-picture plays. `inline`: in the flow, for a preview of the store.
   */
  placement?: "dock" | "inline";
  className?: string;
}

/** The folded stream row: "Live: mira", which unfolds it. */
function CollapsedPill({
  name,
  words,
  onExpand,
  pillRef,
}: {
  name: string;
  words: LiveBreakWidgetCopy;
  onExpand: () => void;
  pillRef: Ref<HTMLButtonElement>;
}) {
  const layout = useLayout();
  return (
    <button
      ref={pillRef}
      type="button"
      aria-expanded={false}
      onClick={onExpand}
      className={cn(
        "inline-flex cursor-pointer items-center gap-2 self-end rounded-lg px-2.5 py-1.5 text-text-primary hover:bg-bg-hover md:self-start",
        widgetSurface(layout.chrome, "lg"),
        PRESS,
        PRESS_SCALE,
        FOCUS_RING,
      )}
    >
      <span aria-hidden className="size-2 shrink-0 rounded-pill bg-status-danger" />
      <Text as="span" variant="body-b2-strong" className="max-w-40 truncate">
        {words.pill(name)}
      </Text>
      <Icon icon={chevronUpIcon} size={16} className="shrink-0 text-text-secondary" />
    </button>
  );
}

/**
 * The live-breaks widget (boxed's `LiveBreaksDock` and `LiveBreaksWidget`): the corner column the
 * store's frame mounts once, with picture-in-picture on top and the rows below it: the stream row
 * (or its pill), then the hosting or the queue Return row. It draws nothing with neither.
 */
export function LiveBreaksWidget({
  words,
  stream = null,
  collapsed = false,
  onCollapsedChange,
  picker,
  onPick,
  onOpenPip,
  onHide,
  hosting = null,
  queue = null,
  pip,
  placement = "dock",
  className,
}: LiveBreaksWidgetProps) {
  const layout = useLayout();
  const shownQueue = hosting ? null : queue;
  const pillRef = useRef<HTMLButtonElement>(null);
  const rowRef = useRef<HTMLDivElement>(null);
  // A fold or unfold the shopper pressed moves focus to the control that replaced the one pressed.
  const moveFocus = useRef(false);
  useEffect(() => {
    if (!moveFocus.current) return;
    moveFocus.current = false;
    if (collapsed) pillRef.current?.focus();
    else rowRef.current?.querySelector<HTMLElement>("[data-widget-collapse] button")?.focus();
  }, [collapsed]);

  if (!pip && !stream && !hosting && !shownQueue) return null;
  const toggle = (next: boolean) => {
    moveFocus.current = true;
    onCollapsedChange?.(next);
  };
  const dock = placement === "dock";
  const rowMotion =
    "origin-bottom-left transition-[scale,opacity] duration-base ease-enter motion-reduce:transition-none starting:scale-95 starting:opacity-0";

  return (
    <aside
      aria-label={words.label}
      className={cn(
        "pointer-events-none flex flex-col gap-2",
        // A playing stream stays over a live break's page, whose phone layouts cover the screen
        // (z-45), and under dialogs and menus (z-50); without one the dock sits under them all.
        dock && (pip ? "fixed z-[46]" : "fixed z-40"),
        dock && "max-md:inset-x-2 md:bottom-3 md:left-3 lg:bottom-6 lg:left-6",
        dock && (layout.header === "floating" ? "max-md:bottom-26" : "max-md:bottom-18"),
        dock && (pip ? "items-start md:w-[560px]" : "items-end md:w-auto md:items-start"),
        !dock && "items-start",
        className,
      )}
    >
      {pip && <div className="pointer-events-auto w-full">{pip}</div>}
      {(stream || hosting || shownQueue) && (
        // One width for every row from md, so the stack has one right edge whatever its words say.
        <div className="flex w-full flex-col gap-2 md:w-100 md:max-w-[calc(100vw-3rem)] md:items-start">
          {stream && (
            <div
              key={collapsed ? "collapsed" : "expanded"}
              ref={rowRef}
              className={cn(
                "pointer-events-auto flex w-full flex-col",
                // Folded, it hugs its pill, so the row's width never covers the page beside it.
                collapsed && "items-end md:w-auto md:items-start",
                rowMotion,
              )}
            >
              {collapsed ? (
                <CollapsedPill
                  name={stream.name}
                  words={words}
                  onExpand={() => toggle(false)}
                  pillRef={pillRef}
                />
              ) : (
                <LiveBreakWidgetStreamRow
                  stream={stream}
                  words={words}
                  picker={picker}
                  onPick={onPick}
                  onOpenPip={onOpenPip}
                  onHide={onHide}
                  onCollapse={onCollapsedChange ? () => toggle(true) : undefined}
                />
              )}
            </div>
          )}
          {hosting && (
            <div className={cn("pointer-events-auto w-full", rowMotion)}>
              <LiveBreakWidgetReturnRow kind="hosting" row={hosting} words={words} />
            </div>
          )}
          {shownQueue && (
            <div className={cn("pointer-events-auto w-full", rowMotion)}>
              <LiveBreakWidgetReturnRow kind="queue" row={shownQueue} words={words} />
            </div>
          )}
        </div>
      )}
    </aside>
  );
}
